import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

type Save = (next: boolean) => Promise<void>;

type TedStars = {
  overrides: Map<string, boolean>,
  toggle: (procedureIdentifier: string, next: boolean, save: Save) => Promise<void>,
  settledSoFar: () => number,
  /** A list read begun after a save settled holds the saved star, so the click gives way. */
  releaseSettled: (settledBeforeRead: number) => void,
};

const TedStarsContext = createContext<TedStars | null>(null);

export const TedStarsProvider = TedStarsContext.Provider;

/** Rows of one procedure share a star, so a click shows on all of them without a refetch. */
export const useTedStars = (onSaved?: () => void): TedStars => {
  const [overrides, setOverrides] = useState<Map<string, boolean>>(() => new Map());
  const saving = useRef(new Set<string>());
  const settles = useRef(0);
  const settledAt = useRef(new Map<string, number>());

  const setOverride = useCallback((procedureIdentifier: string, starred: boolean | undefined) => {
    setOverrides((current) => {
      const next = new Map(current);
      if (starred === undefined) next.delete(procedureIdentifier);
      else next.set(procedureIdentifier, starred);
      return next;
    });
  }, []);

  const toggle = useCallback(async (procedureIdentifier: string, next: boolean, save: Save) => {
    if (saving.current.has(procedureIdentifier)) return;
    saving.current.add(procedureIdentifier);
    const previous = overrides.get(procedureIdentifier);
    setOverride(procedureIdentifier, next);
    try {
      await save(next);
    } catch (e) {
      setOverride(procedureIdentifier, previous);
      throw e;
    } finally {
      saving.current.delete(procedureIdentifier);
      settles.current += 1;
      settledAt.current.set(procedureIdentifier, settles.current);
    }
    onSaved?.();
  }, [overrides, setOverride, onSaved]);

  const settledSoFar = useCallback(() => settles.current, []);

  const releaseSettled = useCallback((settledBeforeRead: number) => {
    const released = Array.from(settledAt.current)
      .filter(([procedureIdentifier, at]) => (
        at <= settledBeforeRead && !saving.current.has(procedureIdentifier)
      ))
      .map(([procedureIdentifier]) => procedureIdentifier);
    if (!released.length) return;
    released.forEach((procedureIdentifier) => settledAt.current.delete(procedureIdentifier));
    setOverrides((current) => {
      const next = new Map(current);
      released.forEach((procedureIdentifier) => next.delete(procedureIdentifier));
      return next;
    });
  }, []);

  return useMemo(
    () => ({ overrides, toggle, settledSoFar, releaseSettled }),
    [overrides, toggle, settledSoFar, releaseSettled],
  );
};

/** The procedure's star as last clicked, else as saved; one save at a time, undone if it fails. */
export const useTedStar = (procedureIdentifier: string, savedStarred: boolean) => {
  const stars = useContext(TedStarsContext);
  if (!stars) throw new Error("useTedStar needs a TedStarsProvider");
  const starred = stars.overrides.get(procedureIdentifier) ?? savedStarred;
  const toggle = (save: Save) => stars.toggle(procedureIdentifier, !starred, save);
  return { starred, toggle };
};
